import React from "react";
import { InlineInfoBox, XIcon } from "staysure-component-library";

import ButtonGroup from "@/components/atoms/button-group/button-group";
import LabelButtonWrapper, {
  IconButtonWrapper,
} from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import selectedColorBrand from "@/config/tailwind-colors/brand-colors";
import { useRenewalPaymentSummaryStateContext } from "@/providers/renewal-payment-summary-auto-pay-state-provider";
import { CardholderCosentModalType } from "@/schema/renewal-page/payment-summary-page.schema";

type CardholderConsentModalContentProps = {
  onCancel?: () => void;
  handleContinue?: () => void;
} & CardholderCosentModalType;

function CardholderConsentModalContent({
  onCancel,
  handleContinue,
  cancelButtonLabel,
  confirmButtonLabel,
  infoBoxContentPolicyholderNotCardholder,
  infoBoxContentDoNotSaveCard,
  modalTitle,
  noButtonLabel,
  questionOneDescription,
  questionTwoDescription,
  yesButtonLabel,
}: Readonly<CardholderConsentModalContentProps>) {
  const [isCardholderConsent, setIsCardholderConsent] = React.useState<
    number | null
  >(null);
  const [saveCardForFuture, setSaveCardForFuture] = React.useState<
    number | null
  >(null);

  const { setIsCardHolder, setIsFuturePayment } =
    useRenewalPaymentSummaryStateContext();

  // Decide which info box content to show based on answers
  const infoBoxContentToShow = React.useMemo(() => {
    // Scenario 1: “No” to “Is the policyholder also the cardholder?”
    if (isCardholderConsent === 2) {
      return infoBoxContentPolicyholderNotCardholder;
    }

    // Scenario 2: “Yes” to Q1 and “No” to “Would you like to save this card for future payments?”
    if (isCardholderConsent === 1 && saveCardForFuture === 2) {
      return infoBoxContentDoNotSaveCard;
    }

    return null;
  }, [
    isCardholderConsent,
    saveCardForFuture,
    infoBoxContentPolicyholderNotCardholder,
    infoBoxContentDoNotSaveCard,
  ]);

  return (
    <div className="md:w-[416px] w-[327px]  font-[400] p-spacing-l md:p-spacing-xxxl max-h-[80dvh] overflow-y-auto relative">
      <div className="items-center justify-center px-spacing-xxs py-spacing-xxs gap-spacing-xs w-auto h-auto absolute top-0 right-0">
        {/* Close icon  */}
        <IconButtonWrapper
          data-testid="error-modal-close-icon-button"
          id="error-modal-close-icon-button"
          hierarchy="tertiary"
          btnType="icon"
          icon={
            <XIcon
              stroke={selectedColorBrand["interactive-secondary-default"]}
              testId="closeAPIErrorModalIcon-testid"
            />
          }
          className="w-[48px] h-[48px] rounded-radius-lg py-spacing-s px-spacing-m gap-spacing-xs text-[24px] text-text-on-primary z-[202]"
          onClick={() => {
            if (onCancel !== undefined) {
              onCancel();
            }
          }}
        />
      </div>
      <h1 className="font-header-font text-text-title text-m md:text-3xl leading-[28px] md:leading-custom-size-xl mb-spacing-xs md:mb-spacing-s">
        {modalTitle}
      </h1>
      <p className="text-text-body font-body-font text-xs leading-custom-size-xxl">
        {questionOneDescription}
      </p>

      <ButtonGroup
        setSelectedAnswer={(e) => {
          setIsCardholderConsent(e);
          setIsCardHolder(e === 1);
        }}
        selectedAnswer={isCardholderConsent}
        answerArray={[
          {
            id: 1,
            label: yesButtonLabel,
            value: true,
          },
          {
            id: 2,
            label: noButtonLabel,
            value: false,
          },
        ]}
        location="renewal-cardholder-consent"
        type="renewal-cardholder-consent"
      />
      {isCardholderConsent === 1 && (
        <div className="lg:mt-spacing-xl mt-spacing-l">
          <p className="text-text-body font-body-font text-xs leading-custom-size-xxl">
            {questionTwoDescription}
          </p>

          <ButtonGroup
            setSelectedAnswer={(e) => {
              setSaveCardForFuture(e);
              setIsFuturePayment(e === 1);
            }}
            selectedAnswer={saveCardForFuture}
            answerArray={[
              {
                id: 1,
                label: yesButtonLabel,
                value: true,
              },
              {
                id: 2,
                label: noButtonLabel,
                value: false,
              },
            ]}
            location="renewal-cardholder-consent"
            type="renewal-cardholder-consent"
          />
        </div>
      )}

      {infoBoxContentToShow && (
        <InlineInfoBox
          tabIndex={-1}
          className="px-spacing-m py-spacing-xs w-full font-body-font font-normal
            text-2xs text-text-functional rounded-lg border !border-border-select
            mt-spacing-m mb-0 mx-auto leading-spacing-l bg-surface-info-2"
          content={<RichText richText={infoBoxContentToShow} />}
        />
      )}

      <div className="md:flex md:gap-2 md:justify-between mt-spacing-l">
        <LabelButtonWrapper
          isDisabled={
            (isCardholderConsent === 1 && saveCardForFuture === null) ||
            isCardholderConsent === null
          }
          id="renewal-cardholder-consent-continue-button"
          hierarchy="primary"
          label={confirmButtonLabel}
          className="md:w-[148px] w-full mr-[0px] mb-[8px] md:mb-[0px] text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-text-on-primary"
          onClick={() => {
            if (handleContinue) handleContinue();
          }}
        />

        <LabelButtonWrapper
          id="st-amt-toggle-modal-cancel-button"
          hierarchy="secondary"
          label={cancelButtonLabel}
          className="md:w-[148px] w-full text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-interactive-secondary-default"
          onClick={() => {
            if (onCancel !== undefined) {
              onCancel();
            }
          }}
        />
      </div>
    </div>
  );
}

export default CardholderConsentModalContent;
